import { Combobox } from "@headlessui/react";
import { Tab } from "@headlessui/react";
import { Checkmark, Close, Icon } from "@shared/ui";
import { Fragment, useContext, useRef } from "react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { usePromiseTracker } from "react-promise-tracker";

import { ClientContext } from "../App";
import ButtonDropdownMenu from "../components/ButtonDropdownMenu";
import ButtonPrimary from "../components/ButtonPrimary";
import Form from "../components/Form";
import { useNavigate } from "../hooks/useNavigate";
import Add from "../icons/Add";
import {
  createIgnitionSession,
  createUser,
  getClientIgnitionConfigTemplates,
  getPolicies,
  getUsers,
} from "../lib/automatic";

const NewIgnitionSession = () => {
  const navigate = useNavigate();
  const { promiseInProgress } = usePromiseTracker();

  // Set up ignitionSession types
  const ignitionSessionTypes = ["link", "refresh"];

  // Setup state
  const [error, setError] = useState(null);
  const [user, setUser] = useState();
  const [type, setType] = useState(ignitionSessionTypes[0]);
  const [policies, setPolicies] = useState([]);
  const { selectedClient: clientId } = useContext(ClientContext);
  const [newUser, setnewUser] = useState({});
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [templates, setTemplates] = useState([]);
  // Ignition config template chosen from the dropdown for the pending submit.
  const selectedTemplateIdRef = useRef(null);
  const newUserFormRef = useRef(null);
  const existingUserFormRef = useRef(null);
  // const { user, route } = useAuthenticator((context) => [context.user, context.route]);

  useEffect(() => {
    if (!clientId) {
      return;
    }

    // Reset user state when clientId changes (so that we don't create an ign session for a user that doesn't belong to the client)
    setUser(undefined);
    setQuery("");
    setPolicies([]);
    setUsers([]);
    setnewUser({});
    setError(null);
    setTemplates([]);
    selectedTemplateIdRef.current = null;

    getUsers(clientId).then((data) => setUsers(data));

    getClientIgnitionConfigTemplates(clientId).then(
      ([ignitionTemplates, ignitionTemplatesError]) => {
        if (ignitionTemplatesError) {
          toast.error(
            ignitionTemplatesError.message ||
              "Failed to load ignition templates."
          );
          return;
        }
        if (ignitionTemplates?.templates) {
          setTemplates(ignitionTemplates.templates);
        }
      }
    );
  }, [clientId]);

  // Get policies when user and refresh are selected

  useEffect(() => {
    const loadPolicies = async () => {
      try {
        await getPolicies(clientId).then((data) => setPolicies(data));
      } catch (error) {
        console.log(error);
        setError(error);
      }
    };

    if (user && type === "refresh") {
      loadPolicies();
    }
  }, [user, type, clientId]);

  const filteredUsers =
    query === ""
      ? users
      : users.filter((user) => {
          return (user.firstName + " " + user.lastName)
            .toLowerCase()
            .includes(query.toLowerCase());
        });

  // Setup New Ignition Session form fields
  const newUserFields = [
    {
      id: "firstName",
      label: "First Name",
      type: "text",
      placeholder: "User first name...",
    },
    {
      id: "lastName",
      label: "Last Name",
      type: "text",
      placeholder: "User last name...",
    },
    {
      id: "phone",
      label: "Phone",
      type: "phone",
      placeholder: "10 digit phone number...",
    },
    {
      id: "email",
      label: "Email",
      type: "email",
      placeholder: "User email address...",
    },
  ];

  // Submit a form with the template picked from the dropdown. The template id is
  // stashed on a ref so the native submit handler (and its validation) can read it.
  const submitWithTemplate = (formRef, templateId) => {
    selectedTemplateIdRef.current = templateId;
    formRef.current?.requestSubmit();
  };

  // Dropdown items: "Default" (no template) plus each permitted template. Selecting
  // one submits the given form so field validation still runs before creation.
  // displayName is required when a template is created, but reads aren't
  // re-validated against that schema, so fall back defensively for any
  // legacy/malformed row rather than rendering a blank/"undefined" label.
  const templateMenuItems = (formRef) => [
    { label: "Default", onClick: () => submitWithTemplate(formRef, null) },
    ...templates.map((t) => ({
      label: t.displayName || "Untitled template",
      onClick: () => submitWithTemplate(formRef, t.id),
    })),
  ];

  // Create new user and send ignitionSession link on form submit; return to ignitionSessions
  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const templateId = selectedTemplateIdRef.current;
    try {
      // Create user
      const user = await createUser(newUser, clientId);
      const userId = user.data.id;

      // Create ignition session
      const [ignitionSession, createIgnitionSessionError] =
        await createIgnitionSession(userId, clientId, templateId);
      if (createIgnitionSessionError) {
        throw createIgnitionSessionError;
      }
      const ignitionToken = ignitionSession.ignitionToken;

      // Inform dashboard user of success and navigate to new created session
      toast.success("Ignition Session created.");
      navigate(`/ignition/${ignitionToken}`);
    } catch (error) {
      toast.error(error.message);
      setError(error.message);
      console.error(error);
    }
  };

  const onExistingSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const templateId = selectedTemplateIdRef.current;
    try {
      // Set user id
      if (!user) {
        alert("Please choose a user");
        return;
      }
      const userId = user.id;

      const [ignitionSession, createIgnSessionError] =
        await createIgnitionSession(userId, clientId, templateId);
      if (createIgnSessionError) {
        throw createIgnSessionError;
      }

      // Inform dashboard user of success and navigate to new created session
      toast.success("Ignition Session created.");
      navigate(`/ignition/${ignitionSession.ignitionToken}`);
    } catch (error) {
      toast.error(error.message);
      setError(error.message);
      console.error(error);
    }
  };

  return (
    <div className="w-full h-full bg-white z-10 flex flex-col gap-y-2 absolute">
      <div className="p-8 flex items-center justify-between border-b border-gray-200">
        <h1 className="text-2xl font-bold">New Ignition Session</h1>
        <div className="flex gap-x-4 items-center">
          <Icon
            icon={Close}
            height="h-4"
            width="w-4"
            className="cursor-pointer transition hover:scale-110"
            onClick={() => navigate(-1)}
          />
        </div>
      </div>
      <div className="p-8 flex flex-col gap-y-8">
        <Tab.Group>
          <div className="text-sm font-medium">
            <Tab.List>
              <div className="flex flex-wrap">
                <Tab as={Fragment}>
                  {({ selected }) => (
                    <button
                      className={
                        selected
                          ? "text-left mr-2 inline-block p-4 border-b-2 border-gray-300 active hover:border-gray-300"
                          : "text-left mr-2 inline-block p-4 border-b-2 border-transparent hover:border-gray-300"
                      }
                    >
                      New User
                    </button>
                  )}
                </Tab>
                <Tab as={Fragment}>
                  {({ selected }) => (
                    <button
                      className={
                        selected
                          ? "text-left mr-2 inline-block p-4 border-b-2 border-gray-300 active hover:border-gray-300"
                          : "text-left mr-2 inline-block p-4 border-b-2 border-transparent hover:border-gray-300"
                      }
                    >
                      Existing User
                    </button>
                  )}
                </Tab>
              </div>
            </Tab.List>
          </div>
          <Tab.Panels>
            <Tab.Panel>
              {promiseInProgress === true ? <p>Loading...</p> : null}
              <Form
                name="new-ignition-session"
                fields={newUserFields}
                formData={newUser}
                setFormData={setnewUser}
                onSubmit={onSubmit}
                error={error}
                button="Create Ignition Session"
                formRef={newUserFormRef}
                buttonItems={
                  templates.length > 0
                    ? templateMenuItems(newUserFormRef)
                    : undefined
                }
                alignButtonRight
              />
            </Tab.Panel>
            <Tab.Panel>
              {promiseInProgress === true ? <p>Loading...</p> : null}
              <form
                name="existing-user"
                ref={existingUserFormRef}
                className="flex flex-col gap-y-8 w-4/5 md:w-1/2"
                onSubmit={onExistingSubmit}
              >
                {/* User */}
                <Combobox value={user} onChange={setUser}>
                  <div className="flex flex-col gap-y-2 relative">
                    <label className="text-sm">Select User</label>
                    <div className="relative w-full cursor-default overflow-hidden">
                      <Combobox.Input
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={"Select a user..."}
                        displayValue={(user) =>
                          user && `${user.firstName} ${user.lastName}`
                        }
                        type="text"
                        className="w-full border border-solid border-gray-300 p-[0.625rem] text-sm rounded placeholder-gray-400"
                        required
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-3">
                        <Icon icon={Add} className="cursor-pointer" />
                      </Combobox.Button>
                    </div>

                    <Combobox.Options className="absolute mt-[4.625rem] z-10 max-h-60 w-full overflow-auto bg-white py-1 shadow-lg border border-solid border-gray-300 focus:outline-none text-sm rounded">
                      {filteredUsers.length === 0 && query !== "" ? (
                        <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                          No users found.
                        </div>
                      ) : (
                        filteredUsers.map((user) => (
                          <Combobox.Option
                            key={user.id}
                            value={user}
                            className={({ active }) =>
                              `relative cursor-pointer select-none p-[0.625rem] flex flex-row gap-x-2 items-center ${
                                active
                                  ? "bg-gray-500 text-white fill-white"
                                  : "text-gray-900"
                              }`
                            }
                          >
                            {({ selected, active }) => (
                              <>
                                {selected ? (
                                  <Checkmark className="h-3 w-3" />
                                ) : null}
                                <span
                                  className={`block truncate ${
                                    selected ? "font-medium" : "font-normal"
                                  }`}
                                >
                                  {user.firstName + " " + user.lastName}
                                </span>
                              </>
                            )}
                          </Combobox.Option>
                        ))
                      )}
                    </Combobox.Options>
                  </div>
                </Combobox>

                <div className="self-end">
                  {templates.length > 0 ? (
                    <ButtonDropdownMenu
                      label="Create Ignition Session"
                      items={templateMenuItems(existingUserFormRef)}
                    />
                  ) : (
                    <ButtonPrimary
                      text="Create Ignition Session"
                      type="submit"
                    />
                  )}
                </div>
              </form>
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    </div>
  );
};

export default NewIgnitionSession;
